import React, { useEffect, useState, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { getSharedSpellingList } from '../services/supabase';
import { usePractice } from '../contexts/PracticeContext';
import confetti from 'canvas-confetti';
import { trackEvent } from '../utils/analytics';

/**
 * Component for viewing and importing a shared spelling list
 */
const SharedListView: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const history = useHistory();
  const { importSharedList } = usePractice();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [listName, setListName] = useState('');
  // Add a ref to track if we've already imported
  const [hasImported, setHasImported] = useState(false);

  // Use useCallback to create a stable function reference that won't change on re-renders
  const importSharedListFromSupabase = useCallback(async () => {
    // Prevent multiple imports
    if (hasImported) return;
    
    if (!shareId) {
      setError('Invalid share link');
      setLoading(false);
      return;
    }

    try {
      // Fetch the shared list from Supabase
      const sharedList = await getSharedSpellingList(shareId);
      
      if (!sharedList) {
        setError('Shared list not found or could not be retrieved');
        setLoading(false);
        return;
      }

      // Import the list to the user's local storage
      importSharedList(
        `${sharedList.name} (Shared)`, 
        sharedList.words
      );
      
      // Track shared list import event
      trackEvent('shared_list_imported', {
        list_name: sharedList.name,
        word_count: sharedList.words.length,
        share_id: shareId
      });
      
      setListName(sharedList.name);
      setSuccess(true);
      setLoading(false);
      setHasImported(true);
      
      // Trigger confetti on successful import
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      
      // Redirect to the practice list page after a short delay
      setTimeout(() => {
        history.push('/');
      }, 3000);
    } catch (err) {
      console.error('Error importing shared list:', err);
      setError('Failed to import shared list');
      setLoading(false);
    }
  }, [shareId, importSharedList, history, hasImported]);

  useEffect(() => {
    importSharedListFromSupabase();
  }, [importSharedListFromSupabase]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-purple-100 p-4 sm:p-6">
      <main className="max-w-4xl mx-auto">
        <div className="container mx-auto px-0 py-0 max-w-3xl">
          <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 mb-6 sm:mb-8 border-2 border-indigo-200">
            <h1 className="text-2xl sm:text-3xl font-bold text-center mb-6 text-indigo-800 flex items-center justify-center">
              <span className="text-3xl sm:text-4xl mr-3">📚</span> 
              Shared Spelling List
            </h1>
            
            {loading && (
              <div className="text-center py-8 px-4">
                <div className="relative w-24 h-24 mx-auto mb-6">
                  <div className="absolute top-0 left-0 w-full h-full border-8 border-indigo-200 border-t-indigo-500 rounded-full animate-spin"></div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-3xl">
                    📝
                  </div>
                </div>
                <p className="text-xl text-indigo-700 font-medium mb-2">Loading Magic Words!</p>
                <p className="text-gray-600">Please wait while we import your spelling list...</p>
              </div>
            )}
            
            {error && (
              <div className="bg-pink-100 rounded-xl border-2 border-pink-300 p-6 mb-6 text-center">
                <div className="text-4xl mb-4">😕</div>
                <p className="text-xl text-pink-700 font-bold mb-3">Oops! Something went wrong</p>
                <p className="text-pink-700 mb-4">{error}</p>
                <button 
                  onClick={() => history.push('/')}
                  className="px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-semibold shadow-sm transition-colors flex items-center mx-auto"
                >
                  <span className="text-lg mr-2">🏠</span>
                  Return to Home
                </button>
              </div>
            )}
            
            {success && (
              <div className="bg-gradient-to-r from-green-100 to-teal-100 rounded-xl border-2 border-green-300 p-6 mb-6 text-center">
                <div className="text-5xl mb-4">🎉</div>
                <p className="text-2xl text-green-700 font-bold mb-3">Hooray! List Imported!</p>
                <p className="text-lg text-green-700 mb-2">"{listName}" has been added to your spelling lists.</p>
                <div className="mt-4 flex items-center justify-center space-x-2">
                  <div className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce"></div>
                  <div className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
                <p className="text-sm mt-2 text-indigo-700">Taking you back to your lists...</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <footer className="mt-4 text-center text-indigo-600 text-sm font-medium">
        <p> {new Date().getFullYear()} Happy Spell App</p>
      </footer>
    </div>
  );
};

export default SharedListView;
